import type {
  CurrentUser,
  GridResponse,
  ImportEntity,
  ImportRequest,
  ImportResult,
  LoginChoice,
  LoginRequest,
  ReportResponse,
  SaveGrid,
} from '@quire/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: string;
  code?: string;
  details?: unknown;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
    // Session lives in an httpOnly cookie; nothing is read from JS.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    let body: ErrorBody = {};
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      /* a non-JSON error body is still an error */
    }
    throw new ApiError(
      response.status,
      body.error ?? `Request failed (${response.status})`,
      body.code,
      body.details,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });

export interface ClassSummary {
  id: string;
  name: string;
  isActive: boolean;
  periodCount: number;
  studentCount: number;
  /**
   * Set once the record has been deleted: hidden from its organization, kept in
   * the database. Only ever present for a platform administrator, because the
   * server does not return deleted rows to anybody else.
   */
  deletedAt?: string | null;
}

/** One class with its attached periods and staff. */
export interface ClassDetail {
  id: string;
  name: string;
  isActive: boolean;
  /** Set once deleted. Only ever present for a platform administrator. */
  deletedAt?: string | null;
  /** Marks that would go with this record if it were deleted. */
  markCount: number;
  periods: {
    /** The class_periods row, not the period. */
    id: string;
    periodId: string;
    name: string;
    sortOrder: number;
    isRetired: boolean;
  }[];
  teachers: { id: string; name: string; email: string | null }[];
}

export interface StudentRecord {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  guardianName: string | null;
  guardianContact: string | null;
  isActive: boolean;
  /**
   * Set once the record has been deleted: hidden from its organization, kept in
   * the database. Only ever present for a platform administrator, because the
   * server does not return deleted rows to anybody else.
   */
  deletedAt?: string | null;
}

/**
 * One student with every class they are or were on the roll of.
 *
 * `classIsActive` is the class's state, not the enrolment's: a student can sit
 * on the roll of a class that has since been retired, and the page has to be
 * able to say so rather than showing it as though it were still running.
 */
export interface StudentDetail extends StudentRecord {
  /** Marks that would go with this record if it were deleted. */
  markCount: number;
  enrollments: {
    /** The enrolments row, which is what ends or reopens it. */
    id: string;
    classId: string;
    className: string;
    classIsActive: boolean;
    startDate: string;
    endDate: string | null;
  }[];
}

export interface UserRecord {
  id: string;
  /** Null on a platform account signing in with a username instead. */
  email: string | null;
  username: string | null;
  name: string;
  role: 'org_admin' | 'teacher' | 'super_admin';
  isActive: boolean;
  lastLoginAt: string | null;
  classCount: number;
  /** An unused setup link is outstanding - invited, not yet signed in. */
  pendingSetup: boolean;
  /**
   * Set once the record has been deleted: hidden from its organization, kept in
   * the database. Only ever present for a platform administrator, because the
   * server does not return deleted rows to anybody else.
   */
  deletedAt?: string | null;
}

/**
 * One staff record with the classes they are assigned to.
 *
 * `id` on each row is the class_teachers row, which is what removes the
 * assignment; `classId` is what links to the class.
 */
export interface UserDetail {
  id: string;
  email: string | null;
  username: string | null;
  name: string;
  role: 'org_admin' | 'teacher' | 'super_admin';
  isActive: boolean;
  /** Set once deleted. Only ever present for a platform administrator. */
  deletedAt?: string | null;
  lastLoginAt: string | null;
  pendingSetup: boolean;
  classes: { id: string; classId: string; name: string; isActive: boolean }[];
}

/** A user as seen from platform level, where the organization is not implied. */
export interface PlatformUserRecord {
  id: string;
  email: string | null;
  username: string | null;
  name: string;
  role: 'org_admin' | 'teacher' | 'super_admin';
  isActive: boolean;
  lastLoginAt: string | null;
  organizationId: string | null;
  organizationName: string | null;
  pendingSetup: boolean;
}

/** Returned once when a setup link is generated, and never recoverable after. */
export interface SetupLink {
  url: string;
  expiresAt: string;
}

/** What the setup page shows before asking for a password. */
export interface SetupInvitation {
  name: string;
  identifier: string | null;
  organizationName: string | null;
}

export interface ExceptionRecord {
  id: string;
  classId: string | null;
  date: string;
  reason: string;
}

export interface PeriodRecord {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  /**
   * Set once the record has been deleted: hidden from its organization, kept in
   * the database. Only ever present for a platform administrator, because the
   * server does not return deleted rows to anybody else.
   */
  deletedAt?: string | null;
}

/**
 * A period as the organization list shows it, carrying how many active classes
 * mark it. Separate from PeriodRecord because the create response does not
 * compute the count, and typing it as though it did would be a lie the way
 * UserRecord already lies about what POST /admin/users returns.
 */
export interface PeriodSummary extends PeriodRecord {
  classCount: number;
}

/**
 * One period with the classes whose register it is a column of.
 *
 * `id` on each row is the class_periods row, not the class: that is what
 * detaches it, and confusing the two would delete nothing. `sortOrder` is this
 * period's position on THAT class's register, not its own list position.
 */
export interface PeriodDetail extends PeriodRecord {
  /** Marks that would go with this record if it were deleted. */
  markCount: number;
  classes: {
    id: string;
    classId: string;
    name: string;
    isActive: boolean;
    sortOrder: number;
  }[];
}

export interface OrganizationRecord {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  isActive: boolean;
  userCount: number;
  studentCount: number;
}

export const api = {
  me: () => request<CurrentUser>('/auth/me'),

  /**
   * Signs in from an address alone. Resolves to `{ ok: true }` when the account
   * is unambiguous, or to a `LoginChoice` when the address and password match
   * accounts at more than one organization.
   */
  login: (body: LoginRequest) =>
    request<{ ok: true } | LoginChoice>('/auth/login', { method: 'POST', ...json(body) }),

  adminLogin: (identifier: string, password: string) =>
    request<{ ok: true }>('/auth/login/admin', {
      method: 'POST',
      ...json({ identifier, password }),
    }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST', ...json({}) }),

  classes: {
    list: () => request<{ classes: ClassSummary[] }>('/classes'),
    detail: (classId: string) => request<ClassDetail>(`/classes/${classId}`),
    grid: (classId: string, date: string) =>
      request<GridResponse>(`/classes/${classId}/grid?date=${date}`),
    /**
     * `keepalive` lets the request outlive the page, which is what a flush on
     * the tab being hidden needs. `sendBeacon` cannot serve here: it only ever
     * sends POST, and this is a PUT.
     */
    saveGrid: (classId: string, payload: SaveGrid, options: { keepalive?: boolean } = {}) =>
      request<{ created: number; updated: number; cleared: number; unchanged: number }>(
        `/classes/${classId}/grid`,
        { method: 'PUT', ...json(payload), ...options },
      ),
  },

  reports: {
    attendance: (params: Record<string, string>) =>
      request<ReportResponse>(`/reports/attendance?${new URLSearchParams(params)}`),
  },

  admin: {
    students: () => request<{ students: StudentRecord[] }>('/admin/students'),
    student: (id: string) => request<StudentDetail>(`/admin/students/${id}`),
    createStudent: (body: unknown) =>
      request<StudentRecord>('/admin/students', { method: 'POST', ...json(body) }),
    updateStudent: (id: string, body: unknown) =>
      request<StudentRecord>(`/admin/students/${id}`, { method: 'PATCH', ...json(body) }),
    users: () => request<{ users: UserRecord[] }>('/admin/users'),
    user: (id: string) => request<UserDetail>(`/admin/users/${id}`),
    createUser: (body: unknown) =>
      request<UserRecord & { setup: SetupLink | null }>('/admin/users', {
        method: 'POST',
        ...json(body),
      }),
    updateUser: (id: string, body: unknown) =>
      request<UserRecord>(`/admin/users/${id}`, { method: 'PATCH', ...json(body) }),
    setupLink: (id: string) =>
      request<SetupLink>(`/admin/users/${id}/setup-link`, { method: 'POST', ...json({}) }),
    periods: () => request<{ periods: PeriodSummary[] }>('/admin/periods'),
    period: (id: string) => request<PeriodDetail>(`/admin/periods/${id}`),
    createPeriod: (body: unknown) =>
      request<PeriodRecord>('/admin/periods', { method: 'POST', ...json(body) }),
    // Periods are retired via isActive, never deleted: deleting one would
    // cascade into attendance_records and destroy the history.
    updatePeriod: (id: string, body: unknown) =>
      request(`/admin/periods/${id}`, { method: 'PATCH', ...json(body) }),
    /**
     * Attaches a period to the END of that class's register. One class per
     * call: the class's own page sets its whole ordered list at once through
     * updateClass instead, and only a period attaching itself to several
     * classes needs this.
     */
    attachPeriod: (classId: string, periodId: string) =>
      request(`/admin/classes/${classId}/periods`, { method: 'POST', ...json({ periodId }) }),
    assignTeacher: (classId: string, userId: string) =>
      request(`/admin/classes/${classId}/teachers`, { method: 'POST', ...json({ userId }) }),
    /** Takes the id of the class_teachers row, not of the class or the user. */
    unassignTeacher: (classTeacherId: string) =>
      request(`/admin/class-teachers/${classTeacherId}`, { method: 'DELETE' }),
    /** Takes the id of the class_periods row, not of the class or the period. */
    detachPeriod: (classPeriodId: string) =>
      request(`/admin/class-periods/${classPeriodId}`, { method: 'DELETE' }),
    teachers: () =>
      request<{ teachers: { id: string; name: string; email: string }[] }>('/admin/teachers'),
    createClass: (body: unknown) => request('/admin/classes', { method: 'POST', ...json(body) }),
    updateClass: (id: string, body: unknown) =>
      request(`/admin/classes/${id}`, { method: 'PATCH', ...json(body) }),
    enrollments: (classId: string) =>
      request<{
        enrollments: {
          id: string;
          studentId: string;
          firstName: string;
          lastName: string;
          startDate: string;
          endDate: string | null;
        }[];
      }>(`/admin/enrollments?classId=${classId}`),
    /**
     * Takes either shape: `{ classId, studentIds }` from a class's roll, or
     * `{ studentId, classIds }` from a student's own page. One request either
     * way, so a partial failure cannot leave half a roll behind.
     */
    enrollMany: (body: unknown) =>
      request<{ enrolled: number; skipped: number }>('/admin/enrollments/bulk', {
        method: 'POST',
        ...json(body),
      }),
    // null reopens an enrolment, which is what makes ending one undoable.
    endEnrollment: (id: string, endDate: string | null) =>
      request(`/admin/enrollments/${id}`, { method: 'PATCH', ...json({ endDate }) }),
    schedules: (classId: string) =>
      request<{
        schedules: {
          id: string;
          weekday: number;
          effectiveFrom: string;
          effectiveTo: string | null;
        }[];
      }>(`/admin/schedules?classId=${classId}`),
    createSchedule: (body: unknown) =>
      request('/admin/schedules', { method: 'POST', ...json(body) }),
    /*
     * Ends a recurrence, or reopens it with null. Deleting is still available
     * but is not what the UI does: these rows are the only record that a class
     * was ever expected to meet, so removing one says it never met on Fridays
     * rather than that it stopped.
     */
    updateSchedule: (id: string, effectiveTo: string | null) =>
      request(`/admin/schedules/${id}`, { method: 'PATCH', ...json({ effectiveTo }) }),
    exceptions: (params: Record<string, string>) =>
      request<{ exceptions: ExceptionRecord[] }>(
        `/admin/exceptions?${new URLSearchParams(params)}`,
      ),
    createException: (body: unknown) =>
      request<ExceptionRecord>('/admin/exceptions', { method: 'POST', ...json(body) }),
    deleteException: (id: string) => request(`/admin/exceptions/${id}`, { method: 'DELETE' }),
    /**
     * The file's text, not the file: JSON keeps the request on the same path
     * as every other write, with nothing new for the CSRF check to consider.
     * `commit: false` previews; the answer is the same shape either way.
     */
    importCsv: (entity: ImportEntity, body: ImportRequest) =>
      request<ImportResult>(`/admin/import/${entity}`, { method: 'POST', ...json(body) }),
  },

  platform: {
    organizations: () => request<{ organizations: OrganizationRecord[] }>('/superadmin/orgs'),
    createOrganization: (body: unknown) =>
      request('/superadmin/orgs', { method: 'POST', ...json(body) }),
    updateOrganization: (id: string, body: unknown) =>
      request(`/superadmin/orgs/${id}`, { method: 'PATCH', ...json(body) }),
    enter: (id: string) =>
      request<{ organization: { id: string; name: string; slug: string } }>(
        `/superadmin/orgs/${id}/enter`,
        { method: 'POST', ...json({}) },
      ),
    exit: () => request('/superadmin/exit', { method: 'POST', ...json({}) }),

    users: (params: Record<string, string> = {}) =>
      request<{ users: PlatformUserRecord[] }>(
        `/superadmin/users${Object.keys(params).length > 0 ? `?${new URLSearchParams(params)}` : ''}`,
      ),
    createUser: (body: unknown) =>
      request<PlatformUserRecord & { setup: SetupLink | null }>('/superadmin/users', {
        method: 'POST',
        ...json(body),
      }),
    updateUser: (id: string, body: unknown) =>
      request<PlatformUserRecord>(`/superadmin/users/${id}`, { method: 'PATCH', ...json(body) }),
    setupLink: (id: string) =>
      request<SetupLink>(`/superadmin/users/${id}/setup-link`, { method: 'POST', ...json({}) }),
  },

  /** The unauthenticated password setup flow, reached from an emailed link. */
  setup: {
    read: (token: string) => request<SetupInvitation>(`/setup/${token}`),
    claim: (token: string, password: string) =>
      request<{ ok: true; role: 'org_admin' | 'teacher' | 'super_admin' }>(`/setup/${token}`, {
        method: 'POST',
        ...json({ password }),
      }),
  },
};
